import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AuthBridge } from "./AuthBridge";
import {
  clearAllSessions,
  setAccessTokenSession,
  setRefreshTokenSession,
} from "./common/session-store";
import { googleLogout } from "@react-oauth/google";
import { useAppDispatch } from "../store/hooks";
import { removeUserInfo } from "../features/user/userSlice";

interface AuthContextProps {
  isLoggedIn: boolean;
  login: (accessToken: string, refreshToken: string) => void;
  googleLogin: () => void;
  logout: () => void;
  showLoginModal: boolean;
  setShowLoginModal: (val: boolean) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const dispatch = useAppDispatch();

  /** helper: 檢查 cookie 是否有 g_state */
  const checkGoogleCookie = (): boolean => {
    if (typeof document === "undefined") return false;
    return document.cookie
      .split("; ")
      .some((row) => row.startsWith("g_state="));
  };

  /** 初始化時檢查登入狀態 */
  useEffect(() => {
    const accessToken = sessionStorage.getItem("a");
    const refreshToken = sessionStorage.getItem("r");

    // temp fix in  order to get login modal off landing page
    if (accessToken && refreshToken) {
      setIsLoggedIn(true);
    }
  }, []);

  /** 一般登入 */
  const login = (accessToken: string, refreshToken: string) => {
    setAccessTokenSession(accessToken);
    setRefreshTokenSession(refreshToken);
    setIsLoggedIn(true);
  };

  /** Google Login */
  const googleLogin = () => {
    if (checkGoogleCookie()) {
      setIsLoggedIn(true);
    } else {
      console.warn("Google login cookie not found.");
    }
  };

  /** 登出 → 清掉所有來源 */
  const logout = () => {
    googleLogout();
    document.cookie =
      "g_state=; path=/; domain=.google.com; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    dispatch(removeUserInfo());
    clearAllSessions();
    setIsLoggedIn(false);
  };

  useEffect(() => {
    AuthBridge.registerOpenLoginModal(() => setShowLoginModal(true));
    AuthBridge.registerLogout(logout);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        googleLogin,
        logout,
        showLoginModal,
        setShowLoginModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
